import { useEffect, useRef, useState } from "react";
import GameShell from "../GameShell";

/**
 * Galaxy Defender — CPT/TOVA inspired.
 * Asteroids drift from the top. Blue = target (must shoot), Red = distractor (must NOT shoot).
 * Click/tap to shoot the closest asteroid (or any). 30 seconds.
 */
export default function GalaxyDefender() {
  return (
    <GameShell gameId="galaxy_defender" title="Defensor da Galáxia">
      {({ onFinish }) => <GalaxyGame onFinish={onFinish} />}
    </GameShell>
  );
}

const ROUND_MS = 30000;
const SPAWN_EVERY_MS = 900;
const ASTEROID_SIZE = 60;

function GalaxyGame({ onFinish }) {
  const canvasRef = useRef(null);
  const [countdown, setCountdown] = useState(3);
  const [running, setRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(ROUND_MS);
  const stateRef = useRef({
    asteroids: [],
    hits: 0,
    misses: 0, // shot red asteroid OR missed click
    omissions: 0, // blue went off-screen without being shot
    reactionTimes: [],
    startedAt: 0,
    lastSpawn: 0,
  });

  // Countdown
  useEffect(() => {
    if (countdown <= 0) {
      setRunning(true);
      stateRef.current.startedAt = performance.now();
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 700);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    if (!running) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let rafId;

    const W = canvas.width;
    const H = canvas.height;
    const s = stateRef.current;

    const spawn = () => {
      const x = 40 + Math.random() * (W - 80);
      const isTarget = Math.random() < 0.6;
      s.asteroids.push({
        x,
        y: -ASTEROID_SIZE,
        vy: 90 + Math.random() * 70, // px/sec
        target: isTarget,
        born: performance.now(),
        alive: true,
      });
    };

    const onClick = (ev) => {
      const rect = canvas.getBoundingClientRect();
      const px = ((ev.clientX - rect.left) / rect.width) * W;
      const py = ((ev.clientY - rect.top) / rect.height) * H;
      let nearest = null;
      let nearestDist = Infinity;
      for (const a of s.asteroids) {
        if (!a.alive) continue;
        const dx = a.x - px;
        const dy = a.y - py;
        const d = Math.hypot(dx, dy);
        if (d < ASTEROID_SIZE * 0.9 && d < nearestDist) {
          nearest = a;
          nearestDist = d;
        }
      }
      if (!nearest) {
        s.misses += 1;
        return;
      }
      nearest.alive = false;
      if (nearest.target) {
        s.hits += 1;
        s.reactionTimes.push(performance.now() - nearest.born);
      } else {
        s.misses += 1;
      }
    };

    canvas.addEventListener("pointerdown", onClick);

    let last = performance.now();
    const loop = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      const elapsed = now - s.startedAt;
      const remain = Math.max(0, ROUND_MS - elapsed);
      setTimeLeft(remain);
      if (now - s.lastSpawn > SPAWN_EVERY_MS) {
        s.lastSpawn = now;
        spawn();
      }
      // update
      for (const a of s.asteroids) {
        if (!a.alive) continue;
        a.y += a.vy * dt;
        if (a.y > H + ASTEROID_SIZE) {
          a.alive = false;
          if (a.target) s.omissions += 1;
        }
      }
      // draw
      ctx.fillStyle = "#0b1027";
      ctx.fillRect(0, 0, W, H);
      // stars
      for (let i = 0; i < 40; i++) {
        const sx = (i * 53) % W;
        const sy = (i * 97 + (now / 20) % H) % H;
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.fillRect(sx, sy, 2, 2);
      }
      // cannon
      ctx.fillStyle = "#38BDF8";
      ctx.beginPath();
      ctx.moveTo(W / 2 - 30, H - 10);
      ctx.lineTo(W / 2 + 30, H - 10);
      ctx.lineTo(W / 2, H - 60);
      ctx.closePath();
      ctx.fill();
      // asteroids
      for (const a of s.asteroids) {
        if (!a.alive) continue;
        ctx.beginPath();
        ctx.fillStyle = a.target ? "#38BDF8" : "#EF4444";
        ctx.arc(a.x, a.y, ASTEROID_SIZE / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.6)";
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      if (remain <= 0) {
        cancelAnimationFrame(rafId);
        canvas.removeEventListener("pointerdown", onClick);
        const avg =
          s.reactionTimes.length === 0
            ? 0
            : s.reactionTimes.reduce((a, b) => a + b, 0) / s.reactionTimes.length;
        const accuracy = s.hits + s.misses === 0 ? 0 : s.hits / (s.hits + s.misses);
        const score = Math.max(
          0,
          Math.round(s.hits * 10 - s.misses * 5 - s.omissions * 2)
        );
        onFinish({
          score,
          duration_ms: Math.round(elapsed),
          metrics: {
            hits: s.hits,
            misses: s.misses,
            omissions: s.omissions,
            avg_reaction_ms: Math.round(avg),
            accuracy: Number(accuracy.toFixed(3)),
          },
        });
        return;
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      canvas.removeEventListener("pointerdown", onClick);
    };
  }, [running, onFinish]);

  return (
    <div className="mt-4">
      <div className="text-center mb-3 text-white drop-shadow">
        {running ? (
          <div className="text-lg font-bold">⏱ {(timeLeft / 1000).toFixed(1)}s</div>
        ) : (
          <div className="text-lg">Mire nos azuis, evite os vermelhos!</div>
        )}
      </div>
      <div className="game-canvas-wrap">
        <canvas ref={canvasRef} width={900} height={560} className="game-canvas" data-testid="galaxy-canvas" />
        {!running && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="text-7xl text-white font-bold pop-in">{countdown}</div>
          </div>
        )}
      </div>
    </div>
  );
}
